import { InputButton, type PlayerInput } from "@arcade-hockey/shared";
import type { Vec2 } from "../math/Vec2.js";
import { tuning } from "../state/tuning.js";
import type { MatchState, PlayerState } from "../state/types.js";
import { findActor } from "./seat-control.js";
import { isPuckInLiftZone, isPuckInStickZone } from "./stick.js";

export function applyPokeStrip(state: MatchState, input: PlayerInput): void {
  if ((input.buttons & InputButton.Poke) === 0) return;
  performStrip(state, input, isPuckInStickZone, tuning.puckStrip.pokePopSpeed);
}

export function applyStickLiftStrip(state: MatchState, input: PlayerInput): void {
  if ((input.buttons & InputButton.StickLift) === 0) return;
  performStrip(state, input, isPuckInLiftZone, tuning.puckStrip.stickLiftPopSpeed);
}

function performStrip(
  state: MatchState,
  input: PlayerInput,
  inZone: (defender: PlayerState, puckPos: Vec2) => boolean,
  popSpeed: number,
): void {
  // Phase G push 2: use `findActor` (prefers input.playerId) so bot
  // defenders pressing StickLift resolve to themselves, not whichever
  // player happens to be in the seat. Without this, every bot strip was
  // a silent no-op.
  const defender = findActor(state, input);
  if (defender === null) return;
  if (defender.benched) return;
  if (defender.hasPuck) return;

  // `state.players` is in canonical sorted order (see createMatch); the filter
  // preserves it, so no re-sort is needed.
  const candidates = state.players.filter((p) => p.teamId !== defender.teamId && p.hasPuck);
  const carrier = candidates.find(() => inZone(defender, state.puck.pos));
  if (carrier === undefined) return;
  if (carrier.isProtecting) return;

  carrier.hasPuck = false;
  carrier.possessionQuality = 0;
  state.puck.mode = "Loose";
  state.puck.ownerId = null;
  // Lock out the CARRIER (not the defender) from immediately reclaiming. The
  // defender just earned the strip and should be free to take the loose puck;
  // the carrier is the one fumbling. `noRecaptureUntilTick` gates pickup on
  // `puck.lastTouchedBy`, so recording the carrier here applies the lockout
  // to them — same shape used by the possessionQuality-drop fumble case in
  // possession.ts.
  state.puck.lastTouchedBy = carrier.id;
  state.puck.noRecaptureUntilTick = state.tick + tuning.puck.noRecaptureTicks;
  // Phase H Slice 4 — same-opponent lockout. The stripped carrier
  // can't re-claim possession from the defender's team for the next
  // `sameOpponentRecaptureTicks` ticks, breaking the centre-ice
  // ping-pong cycle.
  state.puck.opponentLockoutPlayerId = carrier.id;
  state.puck.opponentLockoutUntilTick = state.tick + tuning.puck.sameOpponentRecaptureTicks;

  const dx = state.puck.pos.x - defender.pos.x;
  const dy = state.puck.pos.y - defender.pos.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  let popDirX: number;
  let popDirY: number;
  if (dist < 1e-6) {
    popDirX = 1;
    popDirY = 0;
  } else {
    popDirX = dx / dist;
    popDirY = dy / dist;
  }
  state.puck.vel.x = popDirX * popSpeed;
  state.puck.vel.y = popDirY * popSpeed;

  state.events.push({
    type: "PossessionChanged",
    tick: state.tick,
    from: carrier.id,
    to: null,
  });
}
